import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  Inventory as InventoryIcon,
  LocalShipping as ShippingIcon,
  QrCodeScanner as BarcodeIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Visibility as ViewIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../app/layout/DashboardLayout";

interface Order {
  _id: string;
  order_number: string;
  customer_name: string;
  customer_address: string;
  total_amount: number;
  status: string;
  created_at: string;
  items: OrderItem[];
}

interface OrderItem {
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

interface BarcodeScanResult {
  barcode: string;
  product: any;
  success: boolean;
  message: string;
}

const WarehouseDashboard: React.FC = () => {
  const navigate = useNavigate();

  // State management
  const [approvedOrders, setApprovedOrders] = useState<Order[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState("");
  const [scanResults, setScanResults] = useState<BarcodeScanResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [debugData, setDebugData] = useState<any>(null);

  // Load approved orders
  const loadApprovedOrders = useCallback(async () => {
    setLoading(true);
    try {
      console.log("WarehouseDashboard: Loading orders...");
      const apiUrl =
        process.env.REACT_APP_API_URL ||
        "https://orderapp-backend.ssemrah33.workers.dev/api";
      console.log("WarehouseDashboard: API URL:", apiUrl);

      const response = await fetch(`${apiUrl}/orders`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      console.log("WarehouseDashboard: Response status:", response.status);

      if (response.ok) {
        const data = await response.json();
        console.log("WarehouseDashboard: Orders data:", data);

        // Filter approved orders
        const approvedOrders = data.orders.filter(
          (order: any) => order.status === "approved"
        );

        setApprovedOrders(approvedOrders);
        setDebugData(data);
      } else {
        console.error("WarehouseDashboard: Failed to load orders");
        setError("Siparişler yüklenemedi");
      }
    } catch (error) {
      console.error("WarehouseDashboard: Error loading orders:", error);
      setError("Siparişler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  }, []);

  // Otomatik güncelleme için useEffect
  useEffect(() => {
    // Sayfa yüklendiğinde ilk güncelleme
    loadApprovedOrders();

    // Her 10 saniyede bir güncelleme
    const interval = setInterval(() => {
      loadApprovedOrders();
    }, 10000);

    // Cleanup
    return () => clearInterval(interval);
  }, [loadApprovedOrders]);

  // Sayfa görünür olduğunda güncelleme
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadApprovedOrders();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadApprovedOrders]);

  // Barcode scanning functions
  const handleBarcodeScan = async (barcode: string) => {
    try {
      const apiUrl =
        process.env.REACT_APP_API_URL ||
        "https://orderapp-backend.ssemrah33.workers.dev/api";
      const response = await fetch(`${apiUrl}/warehouse/scan-barcode`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ barcode, order_id: selectedOrder?._id }),
      });

      const result = await response.json();

      if (result.success) {
        setScanResults((prev) => [...prev, result]);
        setSuccess(`Barkod okundu: ${result.product.name}`);
      } else {
        setError(result.message || "Barkod okunamadı");
      }
    } catch (err) {
      setError("Barkod okuma hatası");
    }
  };

  const handleManualBarcodeInput = () => {
    if (barcodeInput.trim()) {
      handleBarcodeScan(barcodeInput.trim());
      setBarcodeInput("");
    }
  };

  // Debug function to check all orders
  const handleDebugCheck = async () => {
    try {
      const apiUrl =
        process.env.REACT_APP_API_URL ||
        "https://orderapp-backend.ssemrah33.workers.dev/api";
      const response = await fetch(`${apiUrl}/debug/all-orders`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Debug data:", data);
        setDebugData(data);
      } else {
        console.error("Debug request failed");
      }
    } catch (error) {
      console.error("Debug error:", error);
    }
  };

  // Debug function to approve first pending order
  const handleDebugApprove = async () => {
    try {
      const apiUrl =
        process.env.REACT_APP_API_URL ||
        "https://orderapp-backend.ssemrah33.workers.dev/api";
      const response = await fetch(`${apiUrl}/debug/approve-first-order`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Debug approve result:", data);
        if (data.success) {
          setSuccess(data.message);
          // Reload orders after approval
          loadApprovedOrders();
        } else {
          setError(data.message);
        }
      } else {
        console.error("Debug approve request failed");
      }
    } catch (error) {
      console.error("Debug approve error:", error);
    }
  };

  // Debug function to create test orders
  const handleDebugCreateTestOrders = async () => {
    try {
      const apiUrl =
        process.env.REACT_APP_API_URL ||
        "https://orderapp-backend.ssemrah33.workers.dev/api";
      const response = await fetch(`${apiUrl}/debug/create-test-orders`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Debug create test orders result:", data);
        if (data.success) {
          setSuccess(data.message);
          // Reload orders after creation
          loadApprovedOrders();
          // Reload debug data
          handleDebugCheck();
        } else {
          setError(data.message);
        }
      } else {
        console.error("Debug create test orders request failed");
      }
    } catch (error) {
      console.error("Debug create test orders error:", error);
    }
  };

  const handleCompleteOrder = async () => {
    if (!selectedOrder) return;

    try {
      setLoading(true);
      const apiUrl =
        process.env.REACT_APP_API_URL ||
        "https://orderapp-backend.ssemrah33.workers.dev/api";
      const response = await fetch(
        `${apiUrl}/orders/${selectedOrder._id}/status`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: "prepared" }),
        }
      );

      if (response.ok) {
        setSuccess("Sipariş hazırlandı ve irsaliye oluşturuldu");
        setScanDialogOpen(false);
        setSelectedOrder(null);
        setScanResults([]);
        loadApprovedOrders(); // Refresh orders
      } else {
        setError("Sipariş hazırlanırken hata oluştu");
      }
    } catch (err) {
      setError("Sipariş hazırlanırken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "info";
      case "prepared":
        return "success";
      case "in_transit":
        return "warning";
      case "delivered":
        return "success";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "approved":
        return "Onaylandı";
      case "prepared":
        return "Hazırlandı";
      case "in_transit":
        return "Yolda";
      case "delivered":
        return "Teslim Edildi";
      default:
        return status;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleStartPreparation = (order: Order) => {
    setSelectedOrder(order);
    setScanResults([]);
    setScanDialogOpen(true);
  };

  const handlePrintDeliveryNote = async (orderId: string) => {
    try {
      const apiUrl =
        process.env.REACT_APP_API_URL ||
        "https://orderapp-backend.ssemrah33.workers.dev/api";
      const response = await fetch(
        `${apiUrl}/delivery-notes/generate/${orderId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `irsaliye-${orderId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError("İrsaliye yazdırılırken hata oluştu");
    }
  };

  return (
    <DashboardLayout>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Depo Yönetimi
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Onaylanan siparişleri hazırlayın ve barkod ile stok çıkışı yapın
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleDebugCheck}
            >
              Debug Check
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleDebugApprove}
            >
              Approve First Order
            </Button>
            <Button
              variant="outlined"
              color="info"
              onClick={handleDebugCreateTestOrders}
            >
              Create Test Orders
            </Button>
          </Box>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {/* Debug Data */}
        {debugData && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Debug Info:</strong> User Role: {debugData.user?.role},
              Tenant: {debugData.tenantId}, Total Orders:{" "}
              {debugData.orders?.length || 0}
            </Typography>
            {debugData.orders && debugData.orders.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Orders:</strong>{" "}
                {debugData.orders
                  .map((o: any) => `${o.order_number} (${o.status})`)
                  .join(", ")}
              </Typography>
            )}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <InventoryIcon color="primary" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h6">
                      {loading ? "..." : approvedOrders.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Onaylanan Sipariş
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <ShippingIcon color="success" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h6">
                      {
                        approvedOrders.filter((o) => o.status === "prepared")
                          .length
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Hazırlanan Sipariş
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <TrendingUpIcon color="info" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h6">
                      {formatCurrency(
                        approvedOrders.reduce(
                          (sum, order) => sum + order.total_amount,
                          0
                        )
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toplam Değer
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <AssignmentIcon color="warning" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h6">
                      {approvedOrders.reduce(
                        (sum, order) =>
                          sum +
                          order.items.reduce(
                            (itemSum, item) => itemSum + item.quantity,
                            0
                          ),
                        0
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toplam Ürün
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Approved Orders List */}
        <Paper sx={{ p: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Typography variant="h6">Onaylanan Siparişler</Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadApprovedOrders}
              disabled={loading}
            >
              Yenile
            </Button>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : approvedOrders.length === 0 ? (
            <Alert severity="info">Onaylanan sipariş bulunamadı</Alert>
          ) : (
            <List>
              {approvedOrders.map((order) => (
                <React.Fragment key={order._id}>
                  <ListItem
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <ListItemIcon>
                      <InventoryIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={2}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {order.order_number}
                          </Typography>
                          <Chip
                            label={getStatusLabel(order.status)}
                            color={getStatusColor(order.status) as any}
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Müşteri: {order.customer_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Adres: {order.customer_address}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Tarih: {formatDate(order.created_at)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Toplam: {formatCurrency(order.total_amount)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Ürün Sayısı: {order.items.length} çeşit
                          </Typography>
                        </Box>
                      }
                    />
                    <Box display="flex" gap={1}>
                      <Tooltip title="Sipariş Detayı">
                        <IconButton
                          onClick={() => navigate(`/orders/${order._id}`)}
                          size="small"
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="İrsaliye Yazdır">
                        <IconButton
                          onClick={() => handlePrintDeliveryNote(order._id)}
                          size="small"
                        >
                          <PrintIcon />
                        </IconButton>
                      </Tooltip>
                      <Button
                        variant="contained"
                        startIcon={<BarcodeIcon />}
                        onClick={() => handleStartPreparation(order)}
                        size="small"
                      >
                        Hazırlamaya Başla
                      </Button>
                    </Box>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>

        {/* Barcode Scanning Dialog */}
        <Dialog
          open={scanDialogOpen}
          onClose={() => setScanDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Sipariş Hazırlama - {selectedOrder?.order_number}
          </DialogTitle>
          <DialogContent>
            {selectedOrder && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Müşteri: {selectedOrder.customer_name}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Barkod okuyarak ürünleri sisteme giriş yapın
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Barkod Manuel Giriş"
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && handleManualBarcodeInput()
                    }
                    placeholder="Barkod numarasını girin ve Enter'a basın"
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<BarcodeIcon />}
                    onClick={handleManualBarcodeInput}
                    disabled={!barcodeInput.trim()}
                  >
                    Barkod Oku
                  </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                  Okunan Ürünler ({scanResults.length}/
                  {selectedOrder.items.length})
                </Typography>

                {scanResults.length > 0 ? (
                  <List dense>
                    {scanResults.map((result, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          {result.success ? (
                            <CheckIcon color="success" />
                          ) : (
                            <WarningIcon color="error" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={result.product?.name || result.barcode}
                          secondary={result.message}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Alert severity="info">
                    Henüz ürün okunmadı. Barkod okuyucu ile ürünleri tarayın.
                  </Alert>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                  Sipariş Ürünleri
                </Typography>
                <List dense>
                  {selectedOrder.items.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={item.product_name}
                        secondary={`${item.quantity} adet x ${formatCurrency(item.unit_price)} = ${formatCurrency(item.total_price)}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setScanDialogOpen(false)}>İptal</Button>
            <Button
              variant="contained"
              onClick={handleCompleteOrder}
              disabled={loading || scanResults.length === 0}
              startIcon={
                loading ? <CircularProgress size={20} /> : <CheckIcon />
              }
            >
              {loading ? "Hazırlanıyor..." : "Siparişi Tamamla"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default WarehouseDashboard;
